"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  formatFraction,
  formatPolynomial,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import {
  digitCount,
  scientific,
} from "@/lib/tool-kit/number-theory/geometric-sequence";
import {
  type BoundRead,
  isExact,
  type Method,
  parseSummand,
  readBound,
  type Shape,
  type Summand,
  type SumResult,
  sumSeries,
  type Value,
} from "@/lib/tool-kit/number-theory/summation";

/** English strings. A translated content file overrides these through `ui`. */
export const SIGMA_NOTATION_CALCULATOR_STRINGS = {
  expression: "What is being added, f(k)",
  expressionHint:
    "Use one variable, any letter. Write products with * — k*(k+1) — and powers with ^. Functions: sqrt, abs, ln, log10, exp, sin, cos, tan; constants: pi, e.",
  expressionPlaceholder: "k^2",
  lower: "Lower limit",
  upper: "Upper limit",
  upperHint: "A whole number, or ∞ for an infinite series.",
  results: "Results",
  sumCard: "Sum",
  termsCard: "Terms added",
  methodCard: "Method",
  accuracyCard: "Accuracy",
  exact: "Exact",
  approximate: "Approximate",
  infinite: "Infinitely many",
  methodPolynomial: "Closed form",
  methodGeometric: "Geometric formula",
  methodInfinite: "Infinite geometric series",
  methodDirect: "Term by term",
  methodEmpty: "Empty sum",
  notation: "Σ ({v} = {a} to {b}) of {f} = {sum}",
  notationInfinite: "Σ ({v} = {a} to ∞) of {f} = {sum}",
  emptyNote:
    "The upper limit is below the lower limit, so there are no terms and the sum is 0.",
  polynomialHeading: "Closed form",
  polynomialIntro:
    "The summand is a polynomial in {v}, so the running total has a closed form.",
  polynomialSummand: "f({v}) = {poly}",
  polynomialTotal: "F(n) = Σ ({v} = 1 to n) of f({v}) = {closed}",
  polynomialResult: "F({b}) − F({a1}) = {upper} − {lower} = {sum}",
  geometricHeading: "Geometric series",
  geometricIntro:
    "The summand has the form c · r^{v}, so the sum has a closed form too.",
  geometricParts: "c = {c}, r = {r}",
  geometricFinite: "Σ = c · r^{a} · (1 − r^{m}) ÷ (1 − r), with m = {m} terms",
  geometricInfinite: "Σ = c · r^{a} ÷ (1 − r), because |r| < 1",
  decimalNote: "As a decimal: {value}",
  directNote: "The terms were added one at a time.",
  approxNote:
    "At least one term involves a function, π, or a fractional power, so it was added as a decimal. Treat the last digits as rounded.",
  tableHeading: "Terms",
  colIndex: "Index",
  colTerm: "Term",
  gap: "…",
  undefinedValue: "undefined",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "Σ ({v} = {a} to {b}) of {f} = {sum}",
  enter: "Enter what to add and both limits.",
  badLower: "The lower limit must be a whole number of at most 15 digits.",
  badUpper:
    "The upper limit must be a whole number of at most 15 digits, or ∞.",
  manyVariables: "Use a single index variable. This uses: {names}.",
  unknownFunction:
    "“{token}” followed by a bracket is read as a function name. Write {token}*(…) to multiply, or use one of sqrt, abs, ln, log10, exp, sin, cos, tan.",
  ambiguousLog:
    "Write ln for the natural log or log10 for base 10. Plain log is ambiguous.",
  parseError:
    "That expression could not be read near “{token}”. Use numbers, one variable, + − * / ^, brackets and the listed functions.",
  tooManyTerms:
    "Adding more than 10,000 terms one at a time is not offered, and this summand has no closed form. A polynomial in k or c·r^k can be summed over any range.",
  undefinedTerm:
    "At least one term is undefined — a division by zero or a square root of a negative number — so the sum does not exist as written.",
  diverges:
    "This infinite series diverges: its terms do not shrink to 0 fast enough, so it has no finite sum.",
  needsGeometric:
    "Infinite sums are supported when the terms are geometric, c · r^k with |r| < 1. For other series enter a finite upper limit to see partial sums.",
} as const;

type Strings = typeof SIGMA_NOTATION_CALCULATOR_STRINGS;
type Show = (value: Value | null) => string;

const FUNCTION_CALL = /([A-Za-z_]\w*)\s*\(/;
const EXACT_DIGITS_LIMIT = 40;
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";
const ONE = BigInt(1);

function polynomialIn(coefficients: readonly Rational[], name: string): string {
  return formatPolynomial(
    coefficients
      .map((coefficient, power) => ({
        coefficient,
        variable:
          power === 0
            ? ""
            : `${name}${power === 1 ? "" : toSuperscript(power)}`,
      }))
      .reverse()
  );
}

function methodLabel(method: Method, strings: Strings): string {
  const labels: Record<Method, string> = {
    polynomial: strings.methodPolynomial,
    geometric: strings.methodGeometric,
    "infinite-geometric": strings.methodInfinite,
    direct: strings.methodDirect,
    empty: strings.methodEmpty,
  };
  return labels[method];
}

function ClosedForm({
  result,
  shape,
  vars,
  show,
  strings,
}: {
  result: SumResult;
  shape: Shape;
  vars: { v: string; a: string; b: string };
  show: Show;
  strings: Strings;
}) {
  const lower = BigInt(vars.a);
  if (
    result.method === "polynomial" &&
    shape.kind === "polynomial" &&
    result.endpoints
  ) {
    const values = {
      v: vars.v,
      poly: polynomialIn(shape.coefficients, vars.v),
      closed: result.closedForm ?? "",
      a1: String(lower - ONE),
      b: vars.b,
      upper: show({ exact: result.endpoints.upper }),
      lower: show({ exact: result.endpoints.lower }),
      sum: show(result.value),
    };
    return (
      <>
        <h3 className={WIDGET_H3}>{strings.polynomialHeading}</h3>
        <p className={PROSE_SM}>{t(strings.polynomialIntro, values)}</p>
        <div className={MONO_LINES}>
          <p>{t(strings.polynomialSummand, values)}</p>
          <p>{t(strings.polynomialTotal, values)}</p>
          <p>{t(strings.polynomialResult, values)}</p>
        </div>
      </>
    );
  }
  if (result.geometric) {
    const values = {
      v: vars.v,
      c: show({ exact: result.geometric.c }),
      r: show({ exact: result.geometric.r }),
      a: vars.a,
      m: String(result.count ?? ""),
    };
    const formula =
      result.method === "infinite-geometric"
        ? strings.geometricInfinite
        : strings.geometricFinite;
    return (
      <>
        <h3 className={WIDGET_H3}>{strings.geometricHeading}</h3>
        <p className={PROSE_SM}>{t(strings.geometricIntro, values)}</p>
        <div className={MONO_LINES}>
          <p>{t(strings.geometricParts, values)}</p>
          <p>{t(formula, values)}</p>
        </div>
      </>
    );
  }
  return result.method === "direct" ? (
    <p className={PROSE_SM}>{strings.directNote}</p>
  ) : null;
}

function TermTable({
  result,
  show,
  strings,
}: {
  result: SumResult;
  show: Show;
  strings: Strings;
}) {
  if (result.preview.length === 0) {
    return null;
  }
  const rows: (string | number)[][] = [];
  for (const [index, row] of result.preview.entries()) {
    rows.push([String(row.k), show(row.term)]);
    if (result.gapAfter === index) {
      rows.push([strings.gap, strings.gap]);
    }
  }
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable head={[strings.colIndex, strings.colTerm]} rows={rows} />
    </>
  );
}

function Results({
  summand,
  lower,
  upper,
  show,
  strings,
}: {
  summand: Extract<Summand, { ok: true }>;
  lower: bigint;
  upper: bigint | null;
  show: Show;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const outcome = useMemo(
    () => sumSeries(summand.ast, summand.variable, lower, upper),
    [summand, lower, upper]
  );
  if (!outcome.ok) {
    const messages = {
      "too-many-terms": strings.tooManyTerms,
      "undefined-term": strings.undefinedTerm,
      diverges: strings.diverges,
      "needs-geometric": strings.needsGeometric,
    };
    return <ErrorNotice>{messages[outcome.reason]}</ErrorNotice>;
  }
  const v = summand.variable ?? "k";
  const vars = { v, a: String(lower), b: upper === null ? "∞" : String(upper) };
  const sumText = show(outcome.value);
  const line = t(upper === null ? strings.notationInfinite : strings.notation, {
    ...vars,
    f: summand.text,
    sum: sumText,
  });
  const summary = t(strings.summary, {
    ...vars,
    f: summand.text,
    sum: sumText,
  });
  const exact = isExact(outcome.value) && !outcome.approximate;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.sumCard} value={sumText} />
        <StatCard
          label={strings.termsCard}
          value={
            outcome.count === null
              ? strings.infinite
              : fmt(Number(outcome.count))
          }
        />
        <StatCard
          label={strings.methodCard}
          value={methodLabel(outcome.method, strings)}
        />
        <StatCard
          label={strings.accuracyCard}
          value={exact ? strings.exact : strings.approximate}
        />
      </StatGrid>
      <div className={MONO_LINES}>
        <p>{line}</p>
      </div>
      {isExact(outcome.value) && outcome.value.exact.den !== ONE ? (
        <p className={PROSE_SM}>
          {t(strings.decimalNote, {
            value: fmt(toNumberR(outcome.value.exact), {
              maximumSignificantDigits: 12,
            }),
          })}
        </p>
      ) : null}
      {outcome.method === "empty" ? <Notice>{strings.emptyNote}</Notice> : null}
      {exact ? null : <Notice>{strings.approxNote}</Notice>}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      <ClosedForm
        result={outcome}
        shape={outcome.shape}
        show={show}
        strings={strings}
        vars={vars}
      />
      <TermTable result={outcome} show={show} strings={strings} />
    </>
  );
}

function parseFailure(
  summand: Extract<Summand, { ok: false }>,
  raw: string,
  strings: Strings
) {
  if (summand.reason === "many-variables") {
    return t(strings.manyVariables, { names: summand.detail });
  }
  if (summand.detail === "unknown-function") {
    const token = FUNCTION_CALL.exec(raw)?.[1] ?? "k";
    return t(strings.unknownFunction, { token });
  }
  if (summand.detail === "ambiguous-log") {
    return strings.ambiguousLog;
  }
  return t(strings.parseError, { token: raw.trim() });
}

function Outcome({
  rawExpression,
  rawLower,
  rawUpper,
  show,
  strings,
}: {
  rawExpression: string;
  rawLower: string;
  rawUpper: string;
  show: Show;
  strings: Strings;
}) {
  const summand = useMemo(() => parseSummand(rawExpression), [rawExpression]);
  const lower: BoundRead = readBound(rawLower, false);
  const upper: BoundRead = readBound(rawUpper, true);
  if (
    rawExpression.trim() === "" ||
    lower.kind === "empty" ||
    upper.kind === "empty"
  ) {
    return <Notice>{strings.enter}</Notice>;
  }
  if (!summand.ok) {
    return (
      <ErrorNotice>{parseFailure(summand, rawExpression, strings)}</ErrorNotice>
    );
  }
  if (lower.kind !== "ok") {
    return <ErrorNotice>{strings.badLower}</ErrorNotice>;
  }
  if (upper.kind !== "ok" && upper.kind !== "infinity") {
    return <ErrorNotice>{strings.badUpper}</ErrorNotice>;
  }
  return (
    <Results
      lower={lower.value}
      show={show}
      strings={strings}
      summand={summand}
      upper={upper.kind === "ok" ? upper.value : null}
    />
  );
}

export default function SigmaNotationCalculatorWidget() {
  const strings = useToolUi(SIGMA_NOTATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const show: Show = (value) => {
    if (value === null) {
      return strings.undefinedValue;
    }
    if (isExact(value)) {
      if (digitCount(value.exact) <= EXACT_DIGITS_LIMIT) {
        return formatFraction(value.exact);
      }
      // Too long to write out: a plain rounded decimal when it is of ordinary size.
      const size = Math.abs(toNumberR(value.exact));
      return size >= 1e-4 && size < 1e12
        ? `≈ ${fmt(toNumberR(value.exact), { maximumSignificantDigits: 12 })}`
        : scientific(value.exact);
    }
    return `≈ ${fmt(value.approx, { maximumSignificantDigits: 12 })}`;
  };
  const [expression, setExpression] = useState("k^2");
  const [lower, setLower] = useState("1");
  const [upper, setUpper] = useState("10");
  const dExpression = useDeferredValue(expression);
  return (
    <>
      <TextField
        hint={strings.expressionHint}
        id="sig-expression"
        label={strings.expression}
        mono
        onChange={setExpression}
        placeholder={strings.expressionPlaceholder}
        value={expression}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="sig-lower"
          label={strings.lower}
          mono
          numeric
          onChange={setLower}
          value={lower}
        />
        <TextField
          hint={strings.upperHint}
          id="sig-upper"
          label={strings.upper}
          mono
          onChange={setUpper}
          value={upper}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          rawExpression={dExpression}
          rawLower={lower}
          rawUpper={upper}
          show={show}
          strings={strings}
        />
      </section>
    </>
  );
}
